import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChipList from '@components-clean/core/chip-list';
import CoreSplitter from '@components-clean/core/core-splitter';
import { InfoField } from '@components-clean/core/info-field';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { ConvertedControlsHubPanelObservations } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/helper';
import { CmControlsActionsGrid } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/popout/tables/obs-actions/actions.grid';
// eslint-disable-next-line max-len
import { CmControlsObservationsGrid } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/popout/tables/obs-actions/observations.grid';
// eslint-disable-next-line max-len
import { CmControlsReviewLogsGrid } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/popout/tables/obs-actions/review-logs.grid';
import { SplitterOrientation } from '@utils/constants';
import { cm2Panel_OpraQueries } from '@queries/generated/central-monitoring.generated';

enum ObsActionsChipValue {
    ReviewLogs = 1,
    Actions = 2,
}

type Props = {
    studyRef: string;
    panelData?: cm2Panel_OpraQueries;
    dataset: CM2Dataset;
};

const CmControlsChartObsAndActions = ({ studyRef, panelData, dataset }: Props) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.popOut.obsActions' });
    const [selectedChip, setSelectedChip] = useState<ObsActionsChipValue>(ObsActionsChipValue.ReviewLogs);
    const [selectedObservation, setSelectedObservation] = useState<ConvertedControlsHubPanelObservations | undefined>(
        undefined,
    );
    return (
        <div className="k-h-full">
            <CoreSplitter
                className="!k-h-full"
                orientation={SplitterOrientation.vertical}
                data-testid="ram-cm-controls-obs-actions-splitter"
                alignmentValues={[{ size: selectedObservation ? '40%' : '50%', min: '15px' }, { min: '15px' }]}
            >
                <div className="k-h-full k-pb-2">
                    <div className="k-h-full k-card">
                        <CmControlsObservationsGrid
                            studyRef={studyRef}
                            panelData={panelData}
                            setSelected={setSelectedObservation}
                            dataset={dataset}
                        />
                    </div>
                </div>
                <div className="k-h-full k-pt-2">
                    {!selectedObservation && (
                        <InfoField type="Info" message={t('selectObservationInfo')} layout="center" />
                    )}
                    {selectedObservation && (
                        <>
                            <ChipList
                                data-testid="ram-cm-controls-obs-actions-chip-list"
                                data={[
                                    {
                                        text: t('chipList.reviewLogs'),
                                        value: ObsActionsChipValue.ReviewLogs,
                                    },
                                    {
                                        text: t('chipList.actions'),
                                        value: ObsActionsChipValue.Actions,
                                    },
                                ]}
                                value={selectedChip}
                                onChange={(event) => {
                                    const selected = event.value as ObsActionsChipValue;
                                    if (selected) {
                                        setSelectedChip(selected);
                                    }
                                }}
                                className="mb-10"
                            />
                            <div style={{ height: 'calc(100% - 40px)' }}>
                                {selectedChip === ObsActionsChipValue.ReviewLogs && (
                                    <CmControlsReviewLogsGrid
                                        studyRef={studyRef}
                                        panelData={panelData}
                                        observation={selectedObservation}
                                        dataset={dataset}
                                    />
                                )}
                                {selectedChip === ObsActionsChipValue.Actions && (
                                    <CmControlsActionsGrid
                                        studyRef={studyRef}
                                        observationId={selectedObservation.id!}
                                        panelData={panelData}
                                        dataset={dataset}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </CoreSplitter>
        </div>
    );
};

export default CmControlsChartObsAndActions;
